import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { expose } from 'comlink';
import { collection, deleteDoc, doc, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, Query, query, setDoc, startAfter, where } from 'firebase/firestore';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { Note } from '../../note/note.dto';
import { QueryConstraint } from '@angular/fire/firestore';
import { dtoConverter, NOTES_STORE, updateBatch, WW_NOTES_COLLECTION, logger } from '@damofire/data-commons';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseNotesStepsWorker extends AbstractWarehouseDataSyncStepWorker<Note> {
  constructor() {
    super(NOTES_STORE, 'NOTES');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseNotesStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<Note>(this.store, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected async push(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseNotesStepsWorker::push', { division: ctx.division, category: 'sync' });
    return super.push(ctx, onProgress);
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: Note[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    const q: Query = query(
      collection(getFirestore(), WW_NOTES_COLLECTION),
      where('division._id', '==', ctx.division),
      where('audit.createdBy', '==', this.userId),
      orderBy('audit.lastModifiedDate'),
      ...queryConstraints,
    ).withConverter(dtoConverter({ syncId: ctx.syncId }));
    const queryResult = await getDocs(q);

    if (queryResult.docs.length && !this.shouldStopTask) {
      const notes = queryResult.docs.map((d) => d.data());

      logger.info('Downloaded notes from Firestore', { count: notes.length, category: 'sync' });
      await this.idbService.updateAll(NOTES_STORE, notes).catch(console.error);
      return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
    }

    return [null, 0];
  }

  protected async addRemoteData(data: Note[]): Promise<void> {
    for (const note of data) {
      await setDoc(doc(getFirestore(), `${WW_NOTES_COLLECTION}/${note._id}`), note);
    }
  }

  protected async deleteRemoteData(data: Note[]): Promise<void> {
    for (const note of data) {
      await deleteDoc(doc(getFirestore(), `${WW_NOTES_COLLECTION}/${note._id}`));
    }
  }

  protected async updateRemoteData(data: Note[]): Promise<void> {
    await updateBatch(WW_NOTES_COLLECTION, data);
  }

  protected updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    onProgress(0, { processed: downloaded });
    return Promise.resolve(undefined);
  }
}

expose(WarehouseNotesStepsWorker);
